import { useLanguage } from "../context/LanguageContext"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useRef, useState } from "react"
import canalLogo from "../assets/images/logos/canal-suger.svg"
import cancalHr from "../assets/images/logos/cancal-hr.svg"
import wadiLogo from "../assets/images/logos/wadi-group.svg"
import wadiHr from "../assets/images/logos/wadi-hr.svg"
const KraftHeinzHr = new URL("../assets/images/logos/KraftHeinz-hr.png", import.meta.url).href;
const MazayaHr = new URL("../assets/images/logos/Mazaya-hr.png", import.meta.url).href;
const beltoneHr = new URL("../assets/images/logos/beltone-hr.png", import.meta.url).href;
const beltoneLogo = new URL("../assets/images/logos/beltone-financial.jpg", import.meta.url).href;
const KraftHeinz = new URL("../assets/images/logos/KraftHeinz.webp", import.meta.url).href;
const Mazaya = new URL("../assets/images/logos/Mazaya.png", import.meta.url).href;
import americanaLogo from "../assets/images/logos/americana-logo.svg"
import americanaHr from "../assets/images/logos/americana-talent.svg"
import award1 from "../assets/images/logos/award1.svg"
import award2 from "../assets/images/logos/award2.svg"


export function EndorsementsSection() {
  const { translation: t, isRTL } = useLanguage()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    direction: isRTL ? "rtl" : "ltr",
    slidesToScroll: 1,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 1 },
    },
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const lastMouseXRef = useRef<number | null>(null)
  const lastScrollTimeRef = useRef(0)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!emblaApi || event.buttons !== 0) return
      if (lastMouseXRef.current === null) {
        lastMouseXRef.current = event.clientX
        return
      }

      const deltaX = event.clientX - lastMouseXRef.current
      if (Math.abs(deltaX) < 20) return

      const now = Date.now()
      if (now - lastScrollTimeRef.current < 100) {
        lastMouseXRef.current = event.clientX
        return
      }

      lastScrollTimeRef.current = now
      lastMouseXRef.current = event.clientX

      const movingRight = deltaX > 0
      if (movingRight) {
        isRTL ? emblaApi.scrollPrev() : emblaApi.scrollNext()
      } else {
        isRTL ? emblaApi.scrollNext() : emblaApi.scrollPrev()
      }
    },
    [emblaApi, isRTL]
  )

  const handleMouseLeave = useCallback(() => {
    lastMouseXRef.current = null
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  // Logo mapping array - matches testimonials by index
  const logoMapping = [
    { logo: wadiLogo, hr: wadiHr },
    { logo: canalLogo, hr: cancalHr },
    { logo: americanaLogo, hr: americanaHr },
    { logo: KraftHeinz, hr: KraftHeinzHr },
    { logo: Mazaya, hr: MazayaHr },
    { logo: beltoneLogo, hr: beltoneHr },
  ]

  const testimonials = t.endorsementsData.map((testimonial, index) => {
    const logos = logoMapping[index] || logoMapping[0] // Default to first logo if index out of range

    return {
      name: testimonial.name,
      jobTitle: testimonial.jobTitle,
      description: testimonial.description,
      logo: logos.logo,
      image: logos.hr,
      text: testimonial.description,
      location: testimonial.jobTitle,
    }
  })

  return (
    <section
      dir={isRTL ? "rtl" : "ltr"}
      className="px-5 py-12 md:px-[4.375rem] md:py-[4.375rem] bg-secondary rounded-3xl"
    >
      <div>
        <h2 className="text-2xl md:text-4xl xl:text-[2.5rem] font-bold text-primary text-center mb-[2.875rem]">
          {t.endorsements}
        </h2>

        {/* Slider */}
        <div className="relative max-w-6xl mx-auto">
          <div className="" ref={emblaRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div className="flex w-[106%] -ms-[3%]">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 md:flex-[0_0_50%] xl:flex-[0_0_33%] px-[0.375rem] md:px-3 xl:px-4"
                >
                  <div
                    className="bg-white rounded-[1.25rem] p-9 h-full flex flex-col items-center text-center drop-shadow-[0px_5px_10px_#004E6440] hover-lift"
                  >
                    {/* Logo */}
                    <img src={testimonial.logo} alt={testimonial.name} className="w-40 h-10 object-contain mb-6 hover-scale" />
                    {/* Green Line on Top */}
                    <div className="w-[1.875rem] h-[3px] bg-softGreen mb-6"></div>

                    {/* User Image */}
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-white mb-3 flex-shrink-0 border-4 border-white">
                      <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>

                    {/* User Info */}
                    <div className="mb-6">
                      <h3 className="font-bold text-lg text-primary">{testimonial.name}</h3>
                      <p className="font-normal text-lg  text-primary">{testimonial.location}</p>
                    </div>

                    {/* Testimonial Text */}
                    <p className="text-blackTwo leading-relaxed md:text-lg text-base font-light flex-grow">{testimonial.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Navigation */}

          <div className="hidden md:flex justify-center items-center gap-2 mt-[2.875rem]">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={`rounded-full transition-all ${index === selectedIndex ? "bg-softGreen w-2.5 h-2.5" : "bg-white w-1.5 h-1.5"
                  }`}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <div className="md:hidden flex justify-center items-center gap-2 mt-[2.875rem]">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={`rounded-full transition-all ${index === selectedIndex ? "bg-softGreen w-2.5 h-2.5" : "bg-white w-1.5 h-1.5"
                  }`}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Awards & Recognition */}
          <div className="flex flex-col items-center mt-9 lg:mt-[5.625rem]">
            <h3 className="font-bold text-primary text-2xl md:text-4xl lg:text-[2.5rem] text-center mb-1.5 md:mb-6">
              {t.Awards}
            </h3>
            <p className="font-normal text-base md:text-xl lg:text-[1.375rem] text-center text-primary px-6 md:px-0">{t.awardsDesc}</p>
            <div className="flex items-center justify-center gap-7 md:gap-32 mt-9 md:mt-[2.875rem] h-[4.25rem]">
              <img src={award1} alt="award1" className="w-28 md:w-auto h-full object-contain hover-scale" />
              <img src={award2} alt="award2" className="w-32 md:w-auto h-full object-contain hover-scale" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
